import type { OrganisationScope } from '@repo/upfetch'

// The Organisation scope, readable by the app server so its first render is already scoped.
export const ORG_COOKIE_NAME = 'org_id'
// The Last-used Organisation. Per browser: it doesn't follow the user to another browser or device.
export const LAST_ORGANISATION_COOKIE_NAME = 'last_org_id'

export type OrganisationCookieName = typeof LAST_ORGANISATION_COOKIE_NAME | typeof ORG_COOKIE_NAME

const MAX_AGE = 60 * 60 * 24 * 365

/** What the root layout knows from the request's cookies, so the first render needs no loading. */
export type OrganisationCookies = {
  lastOrganisationId: number | undefined
  scope: OrganisationScope
}

type CookieReader = { get: (name: OrganisationCookieName) => undefined | { value: string } }

/**
 * Parse a cookie value; anything but a positive integer means no Organisation.
 */
const parseOrganisationId = (value: string | undefined): number | undefined => {
  const organisationId = Number(value)
  return Number.isInteger(organisationId) && organisationId > 0 ? organisationId : undefined
}

/**
 * Read the Organisation scope and the Last-used Organisation; no scope cookie means "All organisations".
 */
export const readOrganisationCookies = (cookies: CookieReader): OrganisationCookies => {
  const organisationId = parseOrganisationId(cookies.get(ORG_COOKIE_NAME)?.value)

  return {
    lastOrganisationId: parseOrganisationId(cookies.get(LAST_ORGANISATION_COOKIE_NAME)?.value),
    scope:
      organisationId === undefined ? { kind: 'all' } : { id: organisationId, kind: 'organisation' },
  }
}

/**
 * Set an Organisation cookie (browser only)
 */
export const setOrganisationCookie = (name: OrganisationCookieName, organisationId: number) => {
  const secure = process.env.NODE_ENV === 'production' ? '; Secure' : ''
  document.cookie = `${name}=${organisationId}; Path=/; Max-Age=${MAX_AGE}; SameSite=Lax${secure}`
}

/**
 * Clear an Organisation cookie (browser only)
 */
export const clearOrganisationCookie = (name: OrganisationCookieName) => {
  document.cookie = `${name}=; Path=/; Max-Age=0`
}
